<script setup>
// Heading + a grid of ContentCard (Figma node 75-3675 lineage). Composes
// SplitHeaderBlock (heading left, cards content-column right) so callers
// only ever supply data, not layout.
import { computed } from 'vue'
import SplitHeaderBlock from '@/components/SplitHeaderBlock.vue'
import ContentCard from '@/components/ContentCard.vue'

const props = defineProps({
  block: { type: Object, required: true },   // { heading, cards: [{ title, body, to, colour?, variant?, image?, imagePosition?, illoVariation? }] }
})

// A splash/program card is already internally two columns (media + text
// side by side) — stacking two of those in the default 2-up grid would cram
// four columns total, so a block made up entirely of either renders single-
// column instead.
const isSplashOnly = computed(() =>
  props.block.cards.length > 0 && props.block.cards.every(c => c.variant === 'splash' || c.variant === 'program')
)
</script>

<template>
  <SplitHeaderBlock :heading="block.heading" class="link-cards">
    <ul class="cards" :class="{ 'splash-only': isSplashOnly }">
      <li v-for="(card, i) in block.cards" :key="i">
        <ContentCard
          :variant="card.variant || 'arrow'"
          :colour="card.colour || 'pink'"
          :title="card.title"
          :body="card.body"
          :to="card.to"
          :image="card.image"
          :image-position="card.imagePosition"
          :image-fit="card.imageFit || 'cover'"
          :illo-variation="card.illoVariation || 'A'"
          class="card"
        />
      </li>
    </ul>
  </SplitHeaderBlock>
</template>

<style lang="scss" scoped>
.cards {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-5;

  @include from-md {
    // minmax(0, ...) — a bare fr track won't shrink below its content's
    // min-content width (jk-31 follow-up; same fix as SplitHeaderBlock/
    // .body-grid this feature), which could otherwise force a card wider
    // than its column and break the grid out of the page layout.
    grid-template-columns: repeat(2, minmax(0, 1fr));

    &.splash-only { grid-template-columns: minmax(0, 1fr); }
  }

  li { display: flex; }
  .card { flex: 1; }
}
</style>
